import { Component, Input, OnInit, numberAttribute } from '@angular/core';
import { Router } from '@angular/router';
import { CultivoService } from '../cultivo.service';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { TwRecintoCultivosViewModel } from '../cultivo.types';
import { tap, catchError, of } from 'rxjs';
import { NavMenuService } from 'src/app/components/core/components/navmenu/navmenu.service';
import { EnumCodigosTwTcAcciones } from 'src/app/components/_importacion/solicitudes/types/enum';
import { ConfirmationService } from 'primeng/api';
import { CultivoDesautorizadoCamService } from '../cultivo-desautorizado-cam.service';
import { AutorizarRecintoPIAEDCamViewModel } from '../../types/common-cam-types';

@Component({
    selector: 'app-cultivo-wrapper',
    templateUrl: './cultivo-wrapper.component.html',
    styleUrls: ['./cultivo-wrapper.component.scss'],
})
export class CultivoWrapperComponent extends ComponentBase implements OnInit {
    @Input({ transform: numberAttribute }) id = 0; // Id del recinto
    @Input() dialogMode = false;
    searching = false;
    recintoCultivos: TwRecintoCultivosViewModel | null;
    superficieCultivada = 0; // Contendrá la suma de las superficies ocupadas por cada cultivo del recinto.
    allowToAutorizar = false;
    campaniasByCultivoDesautorizado = new Map();

    private route = '/campanias/recinto/cultivos';
    private bajaSuccessMessage = 'Cultivo dado de baja correctamente';
    private undoBajaSuccessMessage = 'Anulación de la baja del Cultivo realizada correctamente';
    private successAuthorizeMessage = 'Cultivo autorizado correctamente';
    private deleteSuccessMessage = 'Cultivo eliminado correctamente';

    constructor(
        protected notificacionService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
        private cultivoService: CultivoService,
        private cultivoDesautorizadoCamService: CultivoDesautorizadoCamService,
        private router: Router,
        private navMenuService: NavMenuService,
        private confirmationService: ConfirmationService,
    ) {
        super(authService, notificacionService, exceptionHandler);
    }

    ngOnInit(): void {
        this.processInput();
    }

    private processInput(): void {
        if (this.id) {
            // Id de recinto
            this.getRecintoCultivos(this.id);
        }
    }

    /**
     * Se obtiene la lista de cultivos del recinto
     * @param idRecinto number
     * @returns void
     */
    getRecintoCultivos(idRecinto: number): void {
        if (!idRecinto) {
            return;
        }
        this.searching = false;
        this.cultivoService.getRecintoCultivos(idRecinto).subscribe(results => {
            this.recintoCultivos = results;
            this.superficieCultivada = this.cultivoService.getSuperficieCultivada(this.recintoCultivos.TwCultivoCollection);
            this.searching = true;
            if (!this.dialogMode) {
                this.navMenuService.updateMenuServiceForRecinto(idRecinto);
                // Cuando éste componente está siendo utilizado dentro de una ventana de diálogo (otra funcionalidad de la aplicación), no se permite deshacer "desautorizar" (que sería volver a autorizar el cultivo)
                this.allowToAutorizar = this.getAllowToAutorizar();
            }
        });
    }

    /**
     * Invoked when the user wants to clear the form
     * @return void
     */
    onReset(): void {
        this.searching = false;
        this.recintoCultivos = null;
    }

    /**
     * Redirigimos al formulario de creación de un nuevo cultivo
     * @return void
     */
    onAdd(): void {
        this.router.navigate([this.route, this.recintoCultivos?.Id, 'create']);
    }

    /**
     * Redirigimos al formulario de edición del cultivo.
     * Cuando el parámetro sea null, el backend captura el error y se muestra en pantalla.
     * itemId es el id del cultivo
     * @param itemId number
     * @return void
     */
    onEdit(itemId: number): void {
        this.router.navigate([this.route, this.recintoCultivos?.Id, 'edit', itemId]);
    }

    /**
     * Redirigimos al formulario de visualización del cultivo.
     * Cuando el parámetro sea null, el backend captura el error y se muestra en pantalla.
     * itemId es el id del cultivo
     * @param itemId number
     * @return void
     */
    onView(itemId: number): void {
        this.router.navigate([this.route, this.recintoCultivos?.Id, 'view', itemId]);
    }

    /**
     * Se invoca al core para eliminar un cultivo.
     * Se controlará en el core, que el cultivo no esté vinculado a una Solicitud de Campañas
     * itemId es el id del cultivo
     * @param itemId number
     * @return void
     */
    onDelete(itemId: number): void {
        this.confirmationService.confirm({
            message: 'Se va a eliminar el Cultivo, ¿desea continuar?',
            accept: () => {
                this.delete(itemId);
            },
        });
    }

    private delete(itemId: number): void {
        this.cultivoService
            .delete(itemId)
            .pipe(
                tap(() => {
                    this.addSuccessMessage(this.deleteSuccessMessage);
                    this.getRecintoCultivos(this.recintoCultivos!.Id!);
                }),
                catchError(error => {
                    this.catchServerError(error);
                    return of(null);
                }),
            )
            .subscribe();
    }

    onDarDeBaja(itemId: number): void {
        this.confirmationService.confirm({
            message: 'Se va a dar de baja el Cultivo, ¿desea continuar?',
            accept: () => {
                this.darDeBaja(itemId);
            },
        });
    }

    /**
     * Se invoca al core para dar de baja el cultivo.
     * Se controlará en el core, que el cultivo/recinto no esté vinculado a un periodo, con plazo activo.
     * itemId es el id del cultivo
     * @param itemId number
     * @return void
     */
    private darDeBaja(itemId: number): void {
        this.cultivoService
            .baja(itemId)
            .pipe(
                tap(() => {
                    this.addSuccessMessage(this.bajaSuccessMessage);
                    this.getRecintoCultivos(this.recintoCultivos!.Id!);
                }),
                catchError(error => {
                    this.catchServerError(error);
                    return of(null);
                }),
            )
            .subscribe();
    }

    /**
     * Se invoca al core para autorizar un cultivo (sacarlo de la lista negra), en la campaña.
     * Id es el id del cultivo
     * IdCampania es el id de la campaña en la que se quiere volver a autorizar el cultivo
     * @param payload AutorizarRecintoPIAEDCamViewModel
     * @return void
     */
    onAuthorize(payload: AutorizarRecintoPIAEDCamViewModel): void {
        if (!payload) {
            return;
        }
        this.confirmationService.confirm({
            message: '¿Quiere volver a autorizar este Cultivo en la campaña?',
            accept: () => {
                // Current logic to perform a confirmation
                this.cultivoDesautorizadoCamService.authorize(payload).subscribe({
                    next: () => {
                        this.addSuccessMessage(this.successAuthorizeMessage);
                        this.getRecintoCultivos(this.recintoCultivos!.Id!);
                    },
                    error: () => {
                        // TODO: scalero1 - probar autorizar un cultivo autorizado y uno ya desautorizado
                        let msg = 'No puede volver a autorizar este Cultivo';
                        this.addErrorMessage(msg);
                    },
                });
            },
        });
    }

    get allowToCreate(): boolean {
        return !this.recintoCultivos?.EsCompleta;
    }

    getAllowToAutorizar(): boolean {
        if (!this.recintoCultivos || !this.recintoCultivos.TwCultivoCollection || this.recintoCultivos.TwCultivoCollection?.length === 0) return false;
        const isAllowed = this.loggedInUser?.getAction(EnumCodigosTwTcAcciones.AUTORIZAR_CULTIVO_CAM).Editable || false;
        return isAllowed;
    }
}
